import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import FloatingCtaBar from '../../components/feature/FloatingCtaBar';
import SiteFooter from '../../components/feature/SiteFooter';

const allCategories = [
  {
    icon: 'ri-robot-2-line',
    title: 'Software – SaaS – AI',
    desc: 'Cloud-based software solutions, SaaS tools, and AI-powered platforms to automate and manage creative tasks.',
    count: 160,
    rating: 4.8,
    reviews: 312,
    grad: 'from-violet-500 to-purple-600',
    image: 'https://public.readdy.ai/ai/img_res/78b018dc60641378800c9d3ac8ce50bf.jpg',
  },
  {
    icon: 'ri-line-chart-line',
    title: 'Finance – Investment',
    desc: 'Financial planning, investment tracking, and wealth management services for individuals and businesses.',
    count: 89,
    rating: 4.6,
    reviews: 187,
    grad: 'from-emerald-500 to-teal-600',
    image: 'https://readdy.ai/api/search-image?query=lush%20green%20finance%20growth%20concept%20with%20golden%20coins%20stacks%20and%20upward%20trending%20bar%20charts%20emerging%20from%20tropical%20foliage%2C%20vibrant%20emerald%20and%20gold%20color%20palette%2C%20professional%20financial%20success%20concept%20with%20bokeh%20light%20effects%20on%20dark%20green%20background&width=480&height=200&seq=cat_fin_1&orientation=landscape',
  },
  {
    icon: 'ri-shopping-cart-2-line',
    title: 'Ecommerce & Shopping',
    desc: 'Online stores, marketplaces, and shopping platforms optimized for every retail need.',
    count: 126,
    rating: 4.7,
    reviews: 254,
    grad: 'from-orange-400 to-amber-500',
    image: 'https://readdy.ai/api/search-image?query=colorful%20ecommerce%20shopping%20concept%20with%20floating%20shopping%20bags%20gift%20boxes%20and%20payment%20cards%20in%20orange%20yellow%20warm%20tones%2C%20vibrant%20retail%20shopping%20festival%20atmosphere%20with%20confetti%20and%20product%20icons%20on%20bright%20warm%20background%2C%20cheerful%20commercial%20illustration%20style&width=480&height=200&seq=cat_eco_1&orientation=landscape',
  },
  {
    icon: 'ri-graduation-cap-line',
    title: 'Education & Learning',
    desc: 'Online courses, learning platforms, certification programs, and educational resources.',
    count: 94,
    rating: 4.5,
    reviews: 203,
    grad: 'from-sky-500 to-blue-600',
    image: 'https://readdy.ai/api/search-image?query=vibrant%20education%20concept%20with%20open%20books%20floating%20graduation%20cap%20diploma%20scroll%20surrounded%20by%20bright%20colorful%20pencils%20and%20stars%20on%20sky%20blue%20background%2C%20cheerful%20academic%20learning%20atmosphere%20with%20knowledge%20symbols%20and%20light%20beams%2C%20digital%20education%20illustration&width=480&height=200&seq=cat_edu_1&orientation=landscape',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Health – Fitness – Wellness',
    desc: 'Fitness tracking software, apps, and health management solutions for active lifestyles.',
    count: 62,
    rating: 4.4,
    reviews: 142,
    grad: 'from-pink-500 to-rose-600',
    image: 'https://readdy.ai/api/search-image?query=energetic%20health%20and%20fitness%20concept%20with%20glowing%20heart%20pulse%20icon%20surrounded%20by%20colorful%20workout%20equipment%20dumbbells%20yoga%20mat%20and%20healthy%20food%20on%20vibrant%20red%20pink%20gradient%20background%2C%20dynamic%20wellness%20lifestyle%20imagery%20with%20geometric%20shapes&width=480&height=200&seq=cat_hlth_1&orientation=landscape',
  },
  {
    icon: 'ri-sparkling-2-line',
    title: 'Beauty – Fashion – Lifestyle',
    desc: 'Beauty products, fashion discovery tools, trend platforms, and lifestyle brand resources.',
    count: 47,
    rating: 4.6,
    reviews: 119,
    grad: 'from-fuchsia-500 to-pink-500',
    image: 'https://readdy.ai/api/search-image?query=elegant%20beauty%20and%20fashion%20concept%20with%20luxury%20cosmetics%20lipstick%20perfume%20bottle%20makeup%20brushes%20surrounded%20by%20pink%20flowers%20and%20sparkles%20on%20soft%20rose%20gold%20gradient%20background%2C%20glamorous%20lifestyle%20aesthetics%20with%20pastel%20colors%20and%20light%20bokeh&width=480&height=200&seq=cat_bty_1&orientation=landscape',
  },
  {
    icon: 'ri-home-smile-line',
    title: 'Home – Garden – DIY',
    desc: 'Home improvement, garden design tools, and DIY project planning resources.',
    count: 75,
    rating: 4.3,
    reviews: 98,
    grad: 'from-lime-500 to-green-600',
    image: 'https://readdy.ai/api/search-image?query=cozy%20home%20and%20garden%20concept%20with%20beautiful%20suburban%20house%20surrounded%20by%20lush%20green%20garden%20blooming%20flowers%20and%20garden%20tools%20in%20warm%20sunlight%2C%20idyllic%20residential%20exterior%20with%20vibrant%20nature%20and%20golden%20hour%20lighting%2C%20cheerful%20home%20improvement%20illustration&width=480&height=200&seq=cat_home_1&orientation=landscape',
  },
  {
    icon: 'ri-gamepad-line',
    title: 'Gaming – Entertainment',
    desc: 'Video games, streaming platforms, entertainment apps, and content management solutions.',
    count: 138,
    rating: 4.9,
    reviews: 276,
    grad: 'from-purple-600 to-indigo-700',
    image: 'https://readdy.ai/api/search-image?query=futuristic%20gaming%20entertainment%20concept%20with%20neon%20glowing%20game%20controller%20surrounded%20by%20holographic%20game%20elements%20and%20electric%20purple%20cyan%20light%20effects%20on%20dark%20background%2C%20epic%20gaming%20atmosphere%20with%20dynamic%20energy%20particles%20and%20digital%20circuits&width=480&height=200&seq=cat_gam_1&orientation=landscape',
  },
  {
    icon: 'ri-plane-line',
    title: 'Travel – Lifestyle Services',
    desc: 'Travel booking platforms, lifestyle services, and adventure planning tools for explorers.',
    count: 96,
    rating: 4.5,
    reviews: 167,
    grad: 'from-teal-400 to-cyan-500',
    image: 'https://readdy.ai/api/search-image?query=world%20travel%20concept%20with%20glowing%20globe%20map%20surrounded%20by%20international%20landmarks%20Eiffel%20Tower%20Big%20Ben%20colosseum%20in%20vibrant%20teal%20cyan%20tones%2C%20wanderlust%20travel%20adventure%20with%20passport%20compass%20and%20airline%20icons%20on%20turquoise%20gradient%20background&width=480&height=200&seq=cat_trv_1&orientation=landscape',
  },
  {
    icon: 'ri-group-line',
    title: 'Family – Pet – Kids',
    desc: 'Family activity apps, pet care platforms, and kids educational tools for everyday life.',
    count: 58,
    rating: 4.4,
    reviews: 114,
    grad: 'from-yellow-400 to-orange-400',
    image: 'https://readdy.ai/api/search-image?query=cheerful%20family%20and%20children%20concept%20with%20happy%20family%20silhouettes%20cute%20cartoon%20pets%20dog%20cat%20surrounded%20by%20colorful%20toys%20balloons%20and%20children%20activity%20icons%20on%20warm%20sunny%20yellow%20orange%20gradient%20background%2C%20joyful%20family%20life%20illustration%20with%20playful%20elements&width=480&height=200&seq=cat_fam_1&orientation=landscape',
  },
  {
    icon: 'ri-server-line',
    title: 'Hosting Platforms',
    desc: 'Web hosting services, cloud platforms, server management, and infrastructure solutions.',
    count: 43,
    rating: 4.3,
    reviews: 89,
    grad: 'from-violet-600 to-purple-700',
    image: 'https://readdy.ai/api/search-image?query=futuristic%20digital%20hosting%20concept%20with%20glowing%20server%20racks%20data%20center%20in%20deep%20purple%20and%20electric%20blue%20tones%2C%20abstract%20cloud%20computing%20visualization%20with%20network%20connections%20and%20data%20flow%20streams%20on%20dark%20technology%20background%2C%20cyberpunk%20server%20infrastructure%20art&width=480&height=200&seq=cat_host_1&orientation=landscape',
  },
  {
    icon: 'ri-restaurant-2-line',
    title: 'Food & Beverage',
    desc: 'Recipe apps, food delivery platforms, culinary management, and restaurant tech solutions.',
    count: 52,
    rating: 4.5,
    reviews: 132,
    grad: 'from-teal-500 to-emerald-500',
    image: 'https://readdy.ai/api/search-image?query=vibrant%20food%20and%20beverage%20concept%20with%20delicious%20colorful%20cuisine%20dishes%20fresh%20ingredients%20vegetables%20fruits%20and%20culinary%20tools%20arranged%20on%20elegant%20teal%20background%2C%20gourmet%20food%20photography%20style%20with%20rich%20colors%20and%20appetizing%20presentation&width=480&height=200&seq=cat_food_1&orientation=landscape',
  },
  {
    icon: 'ri-car-line',
    title: 'Automotive & Transportation',
    desc: 'Car reviews, vehicle comparison tools, transportation booking, and logistics platforms.',
    count: 35,
    rating: 4.2,
    reviews: 76,
    grad: 'from-slate-500 to-gray-600',
    image: 'https://readdy.ai/api/search-image?query=sleek%20modern%20automotive%20concept%20with%20luxury%20silver%20sports%20car%20on%20dramatic%20dark%20background%20with%20cinematic%20lighting%20and%20dynamic%20motion%20blur%20effects%2C%20premium%20vehicle%20technology%20with%20glowing%20headlights%20and%20futuristic%20car%20exterior%20design%2C%20automotive%20photography%20style&width=480&height=200&seq=cat_auto_1&orientation=landscape',
  },
  {
    icon: 'ri-building-2-line',
    title: 'Real Estate & Property',
    desc: 'Property listing tools, real estate management, mortgage calculators, and housing platforms.',
    count: 72,
    rating: 4.4,
    reviews: 158,
    grad: 'from-fuchsia-400 to-rose-500',
    image: 'https://readdy.ai/api/search-image?query=elegant%20real%20estate%20property%20concept%20with%20modern%20luxury%20residential%20buildings%20and%20city%20skyline%20in%20pink%20rose%20magenta%20tones%20at%20golden%20hour%2C%20upscale%20property%20development%20with%20architectural%20visualization%20and%20real%20estate%20investment%20theme%2C%20vibrant%20urban%20lifestyle%20concept&width=480&height=200&seq=cat_re_1&orientation=landscape',
  },
];

export default function CategoriesPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'popular'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSignUp, setShowSignUp] = useState(false);
  const [signUpEmail, setSignUpEmail] = useState('');
  const [signUpPassword, setSignUpPassword] = useState('');
  const [signUpDone, setSignUpDone] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: 'Tool Categories – 247digitrend',
    url: `${siteUrl}/categories`,
    description:
      'Browse 14 categories of digital tools including AI, SaaS, finance, ecommerce, education, health, beauty, gaming and more.',
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Categories', item: `${siteUrl}/categories` },
      ],
    },
    hasPart: allCategories.map((cat) => ({
      '@type': 'WebPage',
      name: cat.title,
      description: cat.desc,
    })),
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = allCategories
    .filter((c) => {
      const q = searchQuery.toLowerCase();
      return c.title.toLowerCase().includes(q) || c.desc.toLowerCase().includes(q);
    })
    .sort((a, b) => activeTab === 'popular' ? b.reviews - a.reviews : 0);

  const renderStars = (rating: number) => {
    const full = Math.floor(rating);
    const half = rating - full >= 0.5;
    return Array.from({ length: 5 }).map((_, i) => {
      if (i < full) return <i key={i} className="ri-star-fill text-amber-400 text-xs"></i>;
      if (i === full && half) return <i key={i} className="ri-star-half-line text-amber-400 text-xs"></i>;
      return <i key={i} className="ri-star-line text-gray-300 text-xs"></i>;
    });
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Tool Categories – AI, SaaS, Finance & More | 247digitrend"
        description="Explore 14 categories of digital tools on 247digitrend. Find the best AI tools, SaaS platforms, ecommerce software, finance tools, and more. 1,200+ tools reviewed."
        canonical={`${siteUrl}/categories`}
        keywords="AI tool categories, SaaS categories, digital tool directory, software categories, compare247 categories"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      {showSignUp && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setShowSignUp(false); setSignUpDone(false); }}>
          <div className="relative bg-white rounded-2xl w-full max-w-sm mx-4 p-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors cursor-pointer">
              <i className="ri-close-line text-gray-600 text-base"></i>
            </button>
            {signUpDone ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-4 rounded-full bg-emerald-100">
                  <i className="ri-check-line text-emerald-600 text-2xl"></i>
                </div>
                <h3 className="text-lg font-black text-gray-900 mb-2">Registration Successful!</h3>
                <p className="text-sm text-gray-500">Welcome to 247compare.</p>
                <button onClick={() => { setShowSignUp(false); setSignUpDone(false); }} className="mt-6 w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap">Close</button>
              </div>
            ) : (
              <>
                <div className="text-center mb-6">
                  <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 rounded-xl bg-gradient-to-br from-violet-500 via-fuchsia-500 to-pink-500">
                    <i className="ri-bar-chart-grouped-line text-white text-base"></i>
                  </div>
                  <h3 className="text-xl font-black text-gray-900 mb-1">Create Account</h3>
                  <p className="text-xs text-gray-500">Join 50K+ users of 247compare</p>
                </div>
                <form onSubmit={(e) => { e.preventDefault(); setSignUpDone(true); }} className="space-y-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                    <input type="email" required value={signUpEmail} onChange={(e) => setSignUpEmail(e.target.value)} placeholder="your@email.com" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">Password</label>
                    <input type="password" required value={signUpPassword} onChange={(e) => setSignUpPassword(e.target.value)} placeholder="At least 8 characters" className="w-full px-4 py-2.5 text-sm border border-gray-200 rounded-xl outline-none focus:border-violet-400 transition-colors" />
                  </div>
                  <button type="submit" className="w-full bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap mt-2">Sign Up Now</button>
                </form>
                <p className="text-xs text-center text-gray-400 mt-4">By signing up, you agree to our <a href="#" className="text-violet-600 underline">Terms</a>.</p>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm border-b border-gray-100' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247compare" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-semibold text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-44 pb-14" style={{ background: 'linear-gradient(160deg, #ecfdf5 0%, #f0fdfa 40%, #e0f7ff 100%)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          {/* Breadcrumb */}
          <div className="inline-flex items-center gap-1.5 text-xs text-teal-600 font-semibold mb-5 bg-teal-50 border border-teal-100 rounded-full px-4 py-1.5">
            <i className="ri-hashtag text-teal-500"></i>
            14 Categories
          </div>

          <h1 className="text-5xl font-black text-gray-900 mb-4 tracking-tight leading-tight">
            Explore{' '}
            <span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg, #7c3aed, #a21caf, #db2777)' }}>
              Tool Categories
            </span>
          </h1>
          <p className="text-base text-gray-500 leading-relaxed mb-8 max-w-xl mx-auto">
            Find the right tool for every need — from projects to achievements you can be proud of.
          </p>

          {/* Search */}
          <div className="flex items-center max-w-lg mx-auto bg-white rounded-full border border-gray-200 overflow-hidden px-1" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.07)' }}>
            <i className="ri-search-line text-gray-400 text-base ml-4"></i>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search categories..."
              className="flex-1 px-4 py-3.5 text-sm text-gray-700 placeholder-gray-400 bg-transparent outline-none"
            />
          </div>
        </div>
      </section>

      {/* ── CATEGORIES GRID ── */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-8">

          {/* Section header with tabs */}
          <div className="flex items-center justify-between mb-8">
            <h2 className="text-xl font-black text-gray-900">
              {filtered.length} categories
            </h2>
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setActiveTab('all')}
                className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-all whitespace-nowrap cursor-pointer ${activeTab === 'all' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                All
              </button>
              <button
                onClick={() => setActiveTab('popular')}
                className={`text-xs font-semibold px-4 py-1.5 rounded-full transition-all whitespace-nowrap cursor-pointer ${activeTab === 'popular' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Popular
              </button>
            </div>
          </div>

          {/* Cards grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {filtered.map((cat, i) => (
              <a
                key={i}
                href={cat.title === 'Software \u2013 SaaS \u2013 AI' ? '/categories/software-saas-ai' : cat.title === 'Finance \u2013 Investment' ? '/categories/finance-investment' : cat.title === 'Ecommerce & Shopping' ? '/categories/ecommerce-shopping' : cat.title === 'Education & Learning' ? '/categories/education-learning' : cat.title === 'Health \u2013 Fitness \u2013 Wellness' ? '/categories/health-fitness-wellness' : cat.title === 'Beauty \u2013 Fashion \u2013 Lifestyle' ? '/categories/beauty-fashion-lifestyle' : cat.title === 'Home \u2013 Garden \u2013 DIY' ? '/categories/home-garden-diy' : cat.title === 'Gaming \u2013 Entertainment' ? '/categories/gaming-entertainment' : cat.title === 'Travel \u2013 Lifestyle Services' ? '/categories/travel-lifestyle' : cat.title === 'Family \u2013 Pet \u2013 Kids' ? '/categories/family-pet-kids' : cat.title === 'Hosting Platforms' ? '/categories/hosting-platforms' : cat.title === 'Food & Beverage' ? '/categories/food-beverage' : cat.title === 'Automotive & Transportation' ? '/categories/automotive-transportation' : cat.title === 'Real Estate & Property' ? '/categories/real-estate-property' : '#'}
                className="group bg-white rounded-2xl border border-gray-100 overflow-hidden hover:border-violet-200 transition-all cursor-pointer block"
                style={{ boxShadow: '0 1px 8px rgba(0,0,0,0.05)' }}
              >
                {/* Image area */}
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={cat.image}
                    alt={cat.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Gradient overlay bottom */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>

                  {/* Icon badge top-left */}
                  <div className="absolute top-3 left-3">
                    <div className={`w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.grad} shadow-md`}>
                      <i className={`${cat.icon} text-white text-base`}></i>
                    </div>
                  </div>

                  {/* Tool count badge top-right */}
                  <div className="absolute top-3 right-3">
                    <div className="flex items-center gap-1 bg-white/90 backdrop-blur-sm rounded-full px-2.5 py-1 text-xs font-bold text-gray-700">
                      <i className="ri-tools-line text-gray-500 text-xs"></i>
                      {cat.count} tools
                    </div>
                  </div>
                </div>

                {/* Card body */}
                <div className="p-4">
                  <h3 className="text-sm font-black text-gray-900 mb-1.5 leading-tight">{cat.title}</h3>
                  <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{cat.desc}</p>

                  {/* Rating + See more */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-0.5">
                        {renderStars(cat.rating)}
                      </div>
                      <span className="text-xs text-gray-400">({cat.reviews})</span>
                    </div>
                    <span className="text-xs font-semibold text-violet-600 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform whitespace-nowrap">
                      See more <i className="ri-arrow-right-line text-xs"></i>
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>

          {/* Empty state */}
          {filtered.length === 0 && (
            <div className="text-center py-20">
              <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 rounded-full bg-gray-100">
                <i className="ri-search-line text-2xl text-gray-400"></i>
              </div>
              <p className="text-sm font-semibold text-gray-500">No categories found</p>
              <p className="text-xs text-gray-400 mt-1">Try a different keyword!</p>
            </div>
          )}
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section style={{ background: '#134e4a' }} className="py-14">
        <div className="max-w-4xl mx-auto px-8">
          <div className="grid grid-cols-4 gap-8 text-center">
            {[
              { num: '14', label: 'Categories' },
              { num: '1,200+', label: 'Tools' },
              { num: '50K+', label: 'Reviews' },
              { num: '100K+', label: 'Users' },
            ].map((stat, i) => (
              <div key={i}>
                <div className="text-4xl font-black text-white mb-1">{stat.num}</div>
                <div className="text-sm font-medium text-teal-300">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
